import { useState } from 'react';
import * as Icons from 'lucide-react';
import styles from './IconsPage.module.css';

// Lista de iconos más comunes para mostrar
const commonIcons = [
    'Home', 'User', 'Settings', 'Search', 'Menu', 'X', 'Check', 'ChevronRight',
    'ChevronLeft', 'ChevronDown', 'ChevronUp', 'Plus', 'Minus', 'Edit', 'Trash2',
    'Save', 'Download', 'Upload', 'Mail', 'Phone', 'Calendar', 'Clock', 'MapPin',
    'Heart', 'Star', 'AlertCircle', 'CheckCircle', 'Info', 'AlertTriangle',
    'Eye', 'EyeOff', 'Lock', 'Unlock', 'LogIn', 'LogOut', 'UserPlus', 'Users',
    'File', 'FileText', 'Folder', 'FolderOpen', 'Image', 'Copy', 'Clipboard',
    'Filter', 'RefreshCw', 'MoreVertical', 'MoreHorizontal', 'Bell', 'BellOff',
    'Sun', 'Moon', 'Maximize', 'Minimize', 'ArrowRight', 'ArrowLeft',
    'ArrowUp', 'ArrowDown', 'ExternalLink', 'Link', 'Code', 'Database',
];

export const IconsPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [copiedIcon, setCopiedIcon] = useState<string | null>(null);

    const filteredIcons = commonIcons.filter(icon =>
        icon.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const copyToClipboard = (iconName: string) => {
        const importCode = `import { ${iconName} } from 'lucide-react';\n\n<${iconName} />`;
        navigator.clipboard.writeText(importCode);
        setCopiedIcon(iconName);
        setTimeout(() => setCopiedIcon(null), 2000);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Icons</h1>
                <p className={styles.description}>
                    Biblioteca completa de iconos usando Lucide React. Más de 1000 iconos SVG
                    optimizados, consistentes y personalizables.
                </p>
            </div>

            {/* Información */}
            <section className={styles.section}>
                <h2>Acerca de Lucide React</h2>
                <div className={styles.infoBox}>
                    <p>
                        <strong>Lucide</strong> es una biblioteca de iconos SVG de código abierto, fork de Feather Icons.
                        Todos los iconos son:
                    </p>
                    <ul>
                        <li>✅ SVG optimizados y ligeros</li>
                        <li>✅ Consistentes en estilo y tamaño</li>
                        <li>✅ Personalizables (size, color, stroke-width)</li>
                        <li>✅ Tree-shakeable (solo importas lo que usas)</li>
                        <li>✅ Accesibles con soporte ARIA</li>
                    </ul>
                </div>
            </section>

            {/* Búsqueda */}
            <section className={styles.section}>
                <h2>Explorar Iconos</h2>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Buscar iconos... (ej: user, home, mail)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                    <p className={styles.searchInfo}>
                        Mostrando {filteredIcons.length} de {commonIcons.length} iconos comunes
                    </p>
                </div>

                <div className={styles.iconGrid}>
                    {filteredIcons.map((iconName) => {
                        const IconComponent = (Icons as any)[iconName];
                        if (!IconComponent) return null;

                        return (
                            <div
                                key={iconName}
                                className={styles.iconCard}
                                onClick={() => copyToClipboard(iconName)}
                                title="Click para copiar código"
                            >
                                <div className={styles.iconPreview}>
                                    <IconComponent size={32} />
                                </div>
                                <div className={styles.iconName}>{iconName}</div>
                                {copiedIcon === iconName && (
                                    <div className={styles.copiedBadge}>✓ Copiado!</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Personalización */}
            <section className={styles.section}>
                <h2>Personalización</h2>
                <p>Los iconos aceptan props estándar de React y props específicas:</p>

                <div className={styles.customExamples}>
                    <div className={styles.customExample}>
                        <h3>Tamaño</h3>
                        <div className={styles.iconShowcase}>
                            <Icons.Star size={16} />
                            <Icons.Star size={24} />
                            <Icons.Star size={32} />
                            <Icons.Star size={48} />
                        </div>
                        <code className={styles.code}>
                            {`<Star size={16} />
<Star size={24} />
<Star size={32} />
<Star size={48} />`}
                        </code>
                    </div>

                    <div className={styles.customExample}>
                        <h3>Color</h3>
                        <div className={styles.iconShowcase}>
                            <Icons.Heart color="#e74c3c" size={32} />
                            <Icons.Heart color="#3498db" size={32} />
                            <Icons.Heart color="#2ecc71" size={32} />
                            <Icons.Heart color="#f39c12" size={32} />
                        </div>
                        <code className={styles.code}>
                            {`<Heart color="#e74c3c" />
<Heart color="#3498db" />
<Heart color="#2ecc71" />
<Heart color="#f39c12" />`}
                        </code>
                    </div>

                    <div className={styles.customExample}>
                        <h3>Stroke Width</h3>
                        <div className={styles.iconShowcase}>
                            <Icons.Circle strokeWidth={1} size={32} />
                            <Icons.Circle strokeWidth={2} size={32} />
                            <Icons.Circle strokeWidth={3} size={32} />
                            <Icons.Circle strokeWidth={4} size={32} />
                        </div>
                        <code className={styles.code}>
                            {`<Circle strokeWidth={1} />
<Circle strokeWidth={2} />
<Circle strokeWidth={3} />
<Circle strokeWidth={4} />`}
                        </code>
                    </div>

                    <div className={styles.customExample}>
                        <h3>Fill</h3>
                        <div className={styles.iconShowcase}>
                            <Icons.Star size={32} />
                            <Icons.Star size={32} fill="currentColor" />
                            <Icons.Heart size={32} />
                            <Icons.Heart size={32} fill="#e74c3c" color="#e74c3c" />
                        </div>
                        <code className={styles.code}>
                            {`<Star />
<Star fill="currentColor" />
<Heart />
<Heart fill="#e74c3c" color="#e74c3c" />`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Uso en Componentes */}
            <section className={styles.section}>
                <h2>Uso en Componentes</h2>

                <div className={styles.usageExample}>
                    <h3>En Botones</h3>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button className={styles.btnExample}>
                            <Icons.Plus size={16} />
                            <span>Agregar</span>
                        </button>
                        <button className={styles.btnExample}>
                            <Icons.Download size={16} />
                            <span>Descargar</span>
                        </button>
                        <button className={styles.btnDanger}>
                            <Icons.Trash2 size={16} />
                            <span>Eliminar</span>
                        </button>
                    </div>
                    <code className={styles.code}>
                        {`<button>
  <Plus size={16} />
  <span>Agregar</span>
</button>`}
                    </code>
                </div>

                <div className={styles.usageExample}>
                    <h3>En Inputs</h3>
                    <div className={styles.inputWithIcon}>
                        <Icons.Search size={18} className={styles.inputIcon} />
                        <input type="text" placeholder="Buscar..." className={styles.inputExample} />
                    </div>
                    <code className={styles.code}>
                        {`<div className="input-container">
  <Search size={18} />
  <input type="text" placeholder="Buscar..." />
</div>`}
                    </code>
                </div>
            </section>

            {/* Importación */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`// Importar iconos específicos (recomendado)
import { Home, User, Settings } from 'lucide-react';

// O importar todo (no recomendado, afecta bundle size)
import * as Icons from 'lucide-react';`}
                </code>
            </section>

            {/* Recursos */}
            <section className={styles.section}>
                <h2>Recursos</h2>
                <div className={styles.resources}>
                    <a
                        href="https://lucide.dev/icons/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.resourceLink}
                    >
                        <Icons.ExternalLink size={16} />
                        <span>Galería Completa de Lucide Icons</span>
                    </a>
                    <a
                        href="https://lucide.dev/guide/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.resourceLink}
                    >
                        <Icons.FileText size={16} />
                        <span>Documentación Oficial</span>
                    </a>
                </div>
            </section>

            {/* Iconos ya usados en el sistema */}
            <section className={styles.section}>
                <h2>Iconos Usados en Zafiro ERP</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                    Iconos que ya están siendo utilizados en el sistema:
                </p>
                <div className={styles.systemIcons}>
                    <div className={styles.systemIcon}>
                        <Icons.Menu size={24} />
                        <span>Menu (Sidebar)</span>
                    </div>
                    <div className={styles.systemIcon}>
                        <Icons.X size={24} />
                        <span>X (Cerrar modals)</span>
                    </div>
                    <div className={styles.systemIcon}>
                        <Icons.CheckCircle size={24} />
                        <span>CheckCircle (Toast success)</span>
                    </div>
                    <div className={styles.systemIcon}>
                        <Icons.AlertCircle size={24} />
                        <span>AlertCircle (Toast error)</span>
                    </div>
                    <div className={styles.systemIcon}>
                        <Icons.AlertTriangle size={24} />
                        <span>AlertTriangle (Toast warning)</span>
                    </div>
                    <div className={styles.systemIcon}>
                        <Icons.Info size={24} />
                        <span>Info (Toast info)</span>
                    </div>
                </div>
            </section>
        </div>
    );
};
